/* iter55g21 — Draft Entire Novel readiness dialog.
 *
 * Sits in front of the BulkNovelDraftDialog. On open we score the
 * manuscript on four axes:
 *
 *   1. At least one drafted chapter (so Claude has voice to match).
 *   2. Framework or a synopsis linked (so acts + arcs are known).
 *   3. Characters populated (so cast continuity survives generation).
 *   4. Target words/chapter set to a workable value (>= 400).
 *
 * If the manuscript is thin, the dialog lists what's missing with a
 * short recommendation for each item and a "Continue anyway" escape
 * hatch — writers who know what they're doing can always override. If
 * everything checks out, the readiness dialog auto-forwards to the
 * bulk drafter.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  BookOpen, CheckCircle2, AlertTriangle, ArrowRight, Loader2, Lock, Wrench,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { chaptersApi, charactersApi, frameworkApi, projectsApi } from '@/lib/api';
import BulkNovelDraftDialog from './BulkNovelDraftDialog';

export default function DraftNovelReadinessDialog({
  open, onClose, project, canBulkDraft, upgradeCta,
}) {
  const [loading,   setLoading]   = useState(false);
  const [findings,  setFindings]  = useState(null);
  const [showBulk,  setShowBulk]  = useState(false);
  const [fixing,    setFixing]    = useState(null);   // check-id currently being fixed

  const rescan = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      // Always re-read the project record so any patch we just wrote
      // (e.g. words_per_chapter, from_framework_id) is reflected.
      const fresh = await projectsApi.get(project.id).catch(() => project);
      const [chapters, chars, framework] = await Promise.all([
        chaptersApi.list(fresh.id).catch(() => []),
        charactersApi.list(fresh.id).catch(() => []),
        fresh.from_framework_id
          ? frameworkApi.get(fresh.from_framework_id).catch(() => null)
          : Promise.resolve(null),
      ]);
      const drafted = chapters.filter((c) => (c.content || '').trim());
      const empty   = chapters.length - drafted.length;
      const words   = drafted.reduce((s, c) => s + (c.words || 0), 0);

      const items = [
        {
          id: 'chapters',
          ok: drafted.length >= 1,
          label: `${drafted.length} drafted chapter${drafted.length === 1 ? '' : 's'} · ${words.toLocaleString()} words`,
          howto: 'Write at least one chapter (even a rough one) so Claude can extract your voice — tone, POV, cadence.',
          fixLabel: 'Focus first chapter',
        },
        {
          id: 'framework',
          ok: Boolean(framework || (fresh.synopsis || '').trim().length > 200),
          label: framework
            ? `Linked framework: ${framework.name || framework.id}`
            : (fresh.synopsis ? 'Project has an inline synopsis' : 'No framework or synopsis'),
          howto: 'Open the Framework page (or paste a 3-act synopsis in Story Bible) — Claude uses acts and beats to keep the arc coherent across chapters.',
          fixLabel: 'Create linked framework',
        },
        {
          id: 'characters',
          ok: chars.length >= 1,
          label: `${chars.length} character${chars.length === 1 ? '' : 's'} in the Bible`,
          howto: 'Add at least the protagonist and antagonist to the Character Bible — Claude uses names and traits to keep the cast consistent across chapters.',
          fixLabel: 'Add starter cast',
        },
        {
          id: 'target',
          ok: Number(fresh.words_per_chapter || 0) >= 1500,
          label: `Target: ~${(fresh.words_per_chapter || 0).toLocaleString()} words / chapter`,
          howto: 'Bump the per-chapter target to at least 1,500 words so Claude has room to develop a scene without stopping short.',
          fixLabel: 'Set target to 1,500 words',
        },
      ];
      setFindings({
        items,
        drafted: drafted.length,
        empty,
        chapters: chapters.length,
        project: fresh,
      });
    } catch (e) {
      toast.error('Could not read this project.');
    } finally {
      setLoading(false);
    }
  }, [project]);

  useEffect(() => {
    if (!open || !project?.id) return;
    setFindings(null);
    rescan();
  }, [open, project?.id, rescan]);

  const readiness = useMemo(() => {
    if (!findings?.items) return { ok: 0, total: 0, ready: false };
    const ok    = findings.items.filter((i) => i.ok).length;
    const total = findings.items.length;
    return { ok, total, ready: ok === total };
  }, [findings]);

  // iter55g22 — Inline "Fix now" handlers. Each returns a Promise; we
  // toast-report success, then rescan to reflect the new state.
  const runFix = useCallback(async (id) => {
    if (!findings?.project) return;
    const proj = findings.project;
    setFixing(id);
    // Sonner + Pydantic 422 responses don't mix — the raw `detail` is an
    // array of objects, and toast(...) with an object child crashes the
    // whole app ("Objects are not valid as a React child"). Coerce.
    const errText = (e) => {
      const d = e?.response?.data?.detail;
      if (typeof d === 'string') return d;
      if (Array.isArray(d)) return d.map((x) => x?.msg || (typeof x === 'string' ? x : JSON.stringify(x))).join(' · ');
      return e?.message || 'Could not apply the fix.';
    };
    try {
      if (id === 'target') {
        await projectsApi.update(proj.id, { words_per_chapter: 1500 });
        toast.success('Target set to 1,500 words per chapter.');
      } else if (id === 'characters') {
        // The backend Character model expects traits as [{k,v}] pairs;
        // omit the field entirely so it defaults to []. Writers can add
        // rich traits in the Character Bible afterwards.
        await Promise.all([
          charactersApi.create(proj.id, {
            name: 'Protagonist',
            role: 'Protagonist',
            description: 'The main character. Rewrite this description with their name, age, and one defining desire.',
            bible: {
              identity:    { source: 'Auto-added by Draft-novel readiness dialog' },
              personality: { flaw:   'Add a signature flaw to make them human.' },
            },
          }),
          charactersApi.create(proj.id, {
            name: 'Antagonist',
            role: 'Antagonist',
            description: 'The force opposing the protagonist. Rewrite with a name and a specific goal that clashes with theirs.',
          }),
        ]);
        toast.success('Added Protagonist + Antagonist templates — edit them in the Character Bible.');
      } else if (id === 'framework') {
        const created = await frameworkApi.create({
          name: `${proj.name || 'Untitled'} — Framework`,
          genre: proj.genre || 'literary',
          chapter_count: Number(proj.chapters_count || 20),
          target_words:  Number(proj.words_per_chapter || 1500) * Number(proj.chapters_count || 20),
        });
        try { await projectsApi.update(proj.id, { from_framework_id: created.id }); }
        catch { /* non-fatal */ }
        try { await frameworkApi.patch(created.id, { imported_project_id: proj.id }); }
        catch { /* non-fatal */ }
        toast.success('Framework created and linked. Opening the Chapters section…');
        setTimeout(() => {
          window.location.href = `/framework/${created.id}#chapters`;
        }, 400);
        return;
      } else if (id === 'chapters') {
        toast.info('Focusing the manuscript editor…', {
          description: 'Type any opening sentence — Claude only needs a few paragraphs to catch your voice.',
        });
        onClose?.();
        return;
      }
      await rescan();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setFixing(null);
    }
  }, [findings, onClose, rescan]);

  const proceed = () => {
    setShowBulk(true);
  };

  // --- Locked view (not on Pro+) -----------------------------------------
  if (open && !canBulkDraft) {
    const cta = upgradeCta?.('bulk_draft_novel') || {};
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md" data-testid="draft-novel-locked">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-4 w-4" />
              {cta.title || 'Available on Pro Author'}
            </DialogTitle>
            <DialogDescription className="text-[12px]">
              {cta.body || 'Have Claude draft every remaining chapter back-to-back to your target word count.'}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-panel-border bg-card p-3 text-[11.5px]">
            <b>What you get on {cta.target_tier_label || 'Pro Author'}:</b>
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              <li>Draft entire novels from your framework + drafted chapters.</li>
              <li>Voice profile extraction so every chapter matches your prose.</li>
              <li>Sequential / mid-book review / all-at-once strategies.</li>
              <li>Claude Chat, Story Bible from Synopsis, Bookstore publishing.</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>Not now</Button>
            <Button onClick={() => { window.location.href = '/settings/subscription'; }} data-testid="draft-novel-upgrade-btn">
              See plans
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open && !showBulk} onOpenChange={onClose}>
        <DialogContent
          className="flex max-h-[85vh] max-w-lg flex-col overflow-hidden"
          data-testid="draft-novel-readiness"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-primary" />
              Ready to draft the whole novel?
            </DialogTitle>
            <DialogDescription className="text-[11.5px]">
              A quick pre-flight so Claude has what it needs. You can
              proceed anyway if you disagree with any recommendation.
            </DialogDescription>
          </DialogHeader>

          {loading && (
            <div className="grid place-items-center py-6 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          )}

          {!loading && findings && (
            <div className="flex-1 space-y-2 overflow-auto pr-1">
              <div
                className="rounded-md border p-2 text-[12px]"
                style={{
                  borderColor: readiness.ready ? '#16a34a55' : '#eab30855',
                  backgroundColor: readiness.ready ? '#16a34a12' : '#eab30812',
                }}
              >
                {readiness.ready
                  ? <b>All checks passed — Claude has everything it needs.</b>
                  : <b>{readiness.ok} of {readiness.total} checks pass. See recommendations below.</b>}
                {findings.chapters ? (
                  <div className="mt-0.5 text-[11px] text-muted-foreground">
                    {findings.empty} chapter{findings.empty === 1 ? '' : 's'} still empty · {findings.drafted} drafted.
                  </div>
                ) : null}
              </div>
              {findings.items.map((it) => (
                <div
                  key={it.id}
                  className="flex items-start gap-2 rounded-md border border-panel-border bg-card p-2 text-[11.5px]"
                  data-testid={`draft-novel-check-${it.id}`}
                  data-ok={it.ok ? 'true' : 'false'}
                >
                  {it.ok
                    ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                    : <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />}
                  <div className="flex-1">
                    <div className="font-semibold">{it.label}</div>
                    {!it.ok && (
                      <>
                        <div className="text-muted-foreground">
                          <b>Recommendation:</b> {it.howto}
                        </div>
                        {/* iter55g22 — one-click fix. */}
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => runFix(it.id)}
                          disabled={Boolean(fixing)}
                          data-testid={`draft-novel-fix-${it.id}`}
                          className="mt-1 h-6 gap-1 px-2 text-[10.5px]"
                        >
                          {fixing === it.id
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : <Wrench className="h-3 w-3" />}
                          {fixing === it.id ? 'Applying…' : it.fixLabel || 'Fix now'}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>Close</Button>
            <Button
              onClick={proceed}
              disabled={loading || (findings && findings.drafted === 0 && !readiness.ready)}
              data-testid="draft-novel-proceed"
              className="gap-1"
              title={findings && findings.drafted === 0
                ? 'Draft at least one chapter first — Claude needs a voice sample.'
                : undefined}
            >
              {readiness.ready ? 'Draft the novel' : 'Continue anyway'}
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkNovelDraftDialog
        open={showBulk}
        onClose={() => { setShowBulk(false); onClose?.(); }}
        project={project}
      />
    </>
  );
}
